import { AddIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverProps,
  PopoverTrigger,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { PopUpMessageContext, ScreenContext, useApp, useAuth } from '@hb/react'
import { ASSESSMENTS, colors, FieldMapValue, OnUploadProgress } from '@hb/shared'

import { useFirebase } from '@chem-po/firebase-web'
import { FORM_ERROR } from 'final-form'
import { useCallback, useContext, useMemo } from 'react'
import { processFieldMapData } from '../../../../backend/db/utils/processData'
import { useProfile } from '../../../../contexts/user/ProfileContext'
import { SimpleForm } from '../../../forms'
import { useProfilePopoverMaxHeight } from '../hooks/useProfilePopoverMaxHeight'
import { getReceivedPatientPaymentFields } from './fields'
import { submitReceivedPayment } from './utils'

const AddPaymentContent = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const { processResponse } = useContext(PopUpMessageContext)
  const { assessmentId } = useProfile()

  const { auth } = useFirebase()

  const newPaymentId = useMemo(() => (isOpen ? `${Date.now()}` : null), [isOpen])

  const { appName } = useApp()
  const { admin } = useAuth()

  const { maxHeight, containerRef } = useProfilePopoverMaxHeight(isOpen)

  const field = useMemo(
    () => (newPaymentId ? getReceivedPatientPaymentFields(appName, newPaymentId) : null),
    [appName, newPaymentId],
  )
  const handleSubmit = useCallback(
    async (data: FieldMapValue, onUploadProgress: OnUploadProgress) => {
      const uid = auth?.currentUser?.uid
      if (!uid) return { error: 'Not logged in' }
      if (!assessmentId) {
        return { [FORM_ERROR]: 'No assessment selected' }
      }
      if (!newPaymentId) {
        return { [FORM_ERROR]: 'No payment ID' }
      }
      if (!field) return { [FORM_ERROR]: 'No field' }
      const processed = await processFieldMapData(
        appName,
        admin,
        uid,
        `${ASSESSMENTS}/${assessmentId}/payments/${newPaymentId}`,
        field,
        data,
        undefined,
        onUploadProgress,
      )
      const res = await submitReceivedPayment(
        appName,
        assessmentId,
        newPaymentId,
        processed,
        processed.messageToHb ?? '',
      )
      processResponse(res)
      if (res.success) {
        onClose()
      }
      return res
    },
    [processResponse, assessmentId, onClose, newPaymentId, field, appName, auth, admin],
  )

  return (
    <Box ref={containerRef} maxH={`${maxHeight}px`} overflowY="auto">
      {field ? <SimpleForm theme="detailed" field={field} onSubmit={handleSubmit} /> : null}
    </Box>
  )
}

export const AddPaymentPopover = ({
  buttonSize,
  buttonText = 'PAYMENT',
  placement = 'bottom',
}: {
  buttonSize?: 'sm' | 'xs'
  placement?: PopoverProps['placement']
  buttonText?: string
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isMobile } = useContext(ScreenContext)
  return (
    <Popover
      closeOnBlur={false}
      isOpen={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      isLazy
      placement={isMobile ? 'bottom' : placement}
      strategy="fixed">
      <PopoverTrigger>
        <Box flex={isMobile ? 1 : undefined}>
          <Button
            size={buttonSize ?? (isMobile ? 'xs' : 'sm')}
            fontWeight={300}
            fontFamily="hero-new"
            w={isMobile ? '100%' : undefined}
            bg={colors.green.hex}
            color="gray.800"
            boxShadow="md"
            _hover={{ bg: 'green.300' }}>
            <HStack spacing={0}>
              <AddIcon width={3} color="gray.50" />
              <Text
                height="14px"
                fontFamily="Comfortaa"
                px={1}
                fontSize={isMobile ? 'xs' : 'sm'}
                fontWeight={600}
                color="gray.50">
                {buttonText}
              </Text>
            </HStack>
          </Button>
        </Box>
      </PopoverTrigger>
      <PopoverContent w="auto">
        <PopoverHeader
          color="white"
          textShadow="1px 1px 3px #00000077"
          borderTopRadius={6}
          fontFamily="Comfortaa"
          fontWeight={600}
          bg={colors.green.hex}>
          Add Payment
        </PopoverHeader>
        <PopoverBody overflowY="auto" w="400px" maxW="100vw" p={0}>
          <PopoverCloseButton top={2} color="gray.100" bg="blackAlpha.300" borderRadius="full" />
          <AddPaymentContent isOpen={isOpen} onClose={onClose} />
        </PopoverBody>
        <PopoverArrow bg="green.500" />
      </PopoverContent>
    </Popover>
  )
}
